import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  CloudOff,
  Loader2,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Play,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';

interface UnsyncedAccount {
  accountId: string;
  username: string;
  partner: string;
  geo: string;
  brand: string;
  offer: string;
  currency: string;
  platform: string;
  adwstatsAccountId: string;
  adwstatsAccountName: string;
  adwstatsMatchMethod: string;
  reconciliationEarnings?: number;
  partnerCommissionOurShare?: number;
  invoiceRevenue?: number;
}

type SyncStatus = 'idle' | 'syncing' | 'success' | 'failed';

const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch { /* ignore */ }
  return null;
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const RaketechUnsynced: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState<UnsyncedAccount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncStatuses, setSyncStatuses] = useState<Map<string, SyncStatus>>(new Map());
  const [syncErrors, setSyncErrors] = useState<Map<string, string>>(new Map());
  const [isSyncingAll, setIsSyncingAll] = useState(false);

  const fetchData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed');
      if (json.data.empty) {
        setError(json.data.message);
        setAccounts([]);
        return;
      }

      const unsynced: UnsyncedAccount[] = (json.data.issues as any[])
        .filter(i => i.adwstatsAccountId && i.adwstatsDataSynced === false)
        .map(i => ({
          accountId: i.accountId,
          username: i.username,
          partner: i.partner,
          geo: i.geo,
          brand: i.brand,
          offer: i.offer,
          currency: i.currency,
          platform: i.adwstatsPlatform || '',
          adwstatsAccountId: i.adwstatsAccountId,
          adwstatsAccountName: i.adwstatsAccountName,
          adwstatsMatchMethod: i.adwstatsMatchMethod,
          reconciliationEarnings: i.reconciliationEarnings,
          partnerCommissionOurShare: i.partnerCommissionOurShare,
          invoiceRevenue: i.invoiceRevenue,
        }));

      setAccounts(unsynced);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const syncAccount = useCallback(async (adwstatsId: string) => {
    const token = getAuthToken();
    if (!token) return;

    setSyncStatuses(prev => new Map(prev).set(adwstatsId, 'syncing'));

    try {
      const resp = await fetch(`/api/networks/${adwstatsId}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json() as any;

      if (!resp.ok || !json.success) {
        throw new Error(json.error || json.message || `Sync failed (${resp.status})`);
      }

      setSyncStatuses(prev => new Map(prev).set(adwstatsId, 'success'));
      setSyncErrors(prev => { const n = new Map(prev); n.delete(adwstatsId); return n; });
    } catch (err: any) {
      setSyncStatuses(prev => new Map(prev).set(adwstatsId, 'failed'));
      setSyncErrors(prev => new Map(prev).set(adwstatsId, err.message));
    }
  }, []);

  const syncAll = useCallback(async () => {
    setIsSyncingAll(true);
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }

    const toSync = accounts.filter(a => {
      const status = syncStatuses.get(a.adwstatsAccountId);
      return status !== 'success' && status !== 'syncing';
    });

    toast.info(`Starting sync for ${toSync.length} accounts...`);

    for (const account of toSync) {
      await syncAccount(account.adwstatsAccountId);
      // Small delay between syncs to avoid rate limiting
      await new Promise(r => setTimeout(r, 1500));
    }

    const successes = toSync.filter(a => syncStatuses.get(a.adwstatsAccountId) === 'success').length;
    toast.success(`Sync complete: ${successes}/${toSync.length} successful`);
    setIsSyncingAll(false);
  }, [accounts, syncStatuses, syncAccount]);

  const filtered = useMemo(() => {
    if (!searchTerm) return accounts;
    const q = searchTerm.toLowerCase();
    return accounts.filter(a =>
      a.accountId?.toLowerCase().includes(q) ||
      a.username?.toLowerCase().includes(q) ||
      a.partner?.toLowerCase().includes(q) ||
      a.geo?.toLowerCase().includes(q) ||
      a.platform?.toLowerCase().includes(q) ||
      a.adwstatsAccountName?.toLowerCase().includes(q)
    );
  }, [accounts, searchTerm]);

  const totalReconValue = useMemo(() =>
    accounts.reduce((s, a) => s + (a.reconciliationEarnings || 0), 0),
  [accounts]);

  const syncedCount = useMemo(() =>
    accounts.filter(a => syncStatuses.get(a.adwstatsAccountId) === 'success').length,
  [accounts, syncStatuses]);

  const failedCount = useMemo(() =>
    accounts.filter(a => syncStatuses.get(a.adwstatsAccountId) === 'failed').length,
  [accounts, syncStatuses]);

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <CloudOff className="h-7 w-7 text-amber-400" />
              Unsynced Accounts
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Accounts matched to Raketech data but missing synced statistics in adwstats
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {accounts.length > 0 && (
              <button
                onClick={syncAll}
                disabled={isSyncingAll || accounts.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSyncingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                {isSyncingAll ? 'Syncing...' : `Re-sync All (${accounts.length})`}
              </button>
            )}
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isLoading && accounts.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading account data...</span>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {accounts.length > 0 && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CloudOff className="h-4 w-4 text-amber-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Unsynced</span>
              </div>
              <p className="text-xl font-bold text-amber-400 tabular-nums">{accounts.length}</p>
            </div>
            <div className="rounded-xl border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="h-4 w-4 text-purple-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Recon Value (EUR)</span>
              </div>
              <p className="text-xl font-bold text-purple-400 tabular-nums">{fmtCurrency(totalReconValue)}</p>
            </div>
            {syncedCount > 0 && (
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Synced</span>
                </div>
                <p className="text-xl font-bold text-emerald-400 tabular-nums">{syncedCount}</p>
              </div>
            )}
            {failedCount > 0 && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Failed</span>
                </div>
                <p className="text-xl font-bold text-red-400 tabular-nums">{failedCount}</p>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search accounts..."
              className="pl-8 pr-3 py-1.5 w-full text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            />
          </div>

          {/* Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Adwstats Account</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Platform</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Raketech ID</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Username</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Partner</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">GEO</th>
                    <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Recon (EUR)</th>
                    <th className="text-right px-3 py-2.5 text-zinc-500 font-medium">Partner 95%</th>
                    <th className="text-left px-3 py-2.5 text-zinc-500 font-medium">Match</th>
                    <th className="text-center px-3 py-2.5 text-zinc-500 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                  {filtered.map((acc, idx) => {
                    const status = syncStatuses.get(acc.adwstatsAccountId) || 'idle';
                    const syncError = syncErrors.get(acc.adwstatsAccountId);

                    return (
                      <tr key={`${acc.adwstatsAccountId}-${idx}`} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2.5">
                          <span className="text-white font-medium">{acc.adwstatsAccountName}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          {acc.platform ? (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                              {acc.platform}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-400 tabular-nums">{acc.accountId}</td>
                        <td className="px-3 py-2.5 text-zinc-300">{acc.username}</td>
                        <td className="px-3 py-2.5 text-zinc-400">{acc.partner}</td>
                        <td className="px-3 py-2.5">
                          <span className="text-purple-400 font-medium">{acc.geo}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-amber-400 tabular-nums">
                          {acc.reconciliationEarnings !== undefined ? fmtCurrency(acc.reconciliationEarnings) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-blue-400 tabular-nums">
                          {acc.partnerCommissionOurShare !== undefined ? fmtCurrency(acc.partnerCommissionOurShare) : '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-[10px] text-zinc-500">{acc.adwstatsMatchMethod}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {status === 'idle' && (
                            <button
                              onClick={() => syncAccount(acc.adwstatsAccountId)}
                              disabled={isSyncingAll}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-emerald-400 hover:text-white bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-md transition-colors disabled:opacity-50"
                            >
                              <RefreshCw className="h-3 w-3" />
                              Sync
                            </button>
                          )}
                          {status === 'syncing' && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-amber-400">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Syncing...
                            </span>
                          )}
                          {status === 'success' && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                              <CheckCircle2 className="h-3 w-3" />
                              Done
                            </span>
                          )}
                          {status === 'failed' && (
                            <span className="inline-flex flex-col items-center gap-0.5">
                              <button
                                onClick={() => syncAccount(acc.adwstatsAccountId)}
                                className="inline-flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300"
                              >
                                <XCircle className="h-3 w-3" />
                                Retry
                              </button>
                              {syncError && (
                                <span className="text-[9px] text-red-400/60 max-w-[120px] truncate" title={syncError}>
                                  {syncError}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && accounts.length === 0 && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-emerald-500/20 p-8 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">All Accounts Synced</h2>
          <p className="text-sm text-zinc-400">Every matched account has synced data in adwstats.</p>
        </div>
      )}
    </div>
  );
};

export default RaketechUnsynced;
